(function () {
  // Behaviour for the homepage hero video (sections/hero-video.liquid):
  //
  //   1. a long, eased scroll to the next section when the cue is clicked,
  //   2. fading the cue out once the hero has been scrolled past,
  //   3. pausing the video whenever it is off screen.
  //
  // (3) matters for scroll smoothness as much as for battery: a playing
  // video keeps decoding and compositing frames even with nothing of it
  // visible, which competes with the scroll for main-thread and GPU time.

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SCROLL_DURATION = 1100;

  // Ease in and out symmetrically: no jerk at the start, and it settles onto
  // the target rather than stopping dead.
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function stickyHeaderOffset() {
    var header = document.querySelector('.section-header');
    if (!header) return 0;
    // Only the part of the header that stays on screen should be subtracted.
    var rect = header.getBoundingClientRect();
    return rect.height || 0;
  }

  // Always jump the exact pixel asked for. The eased loop below sets a new
  // position every frame, so if a stylesheet ever puts scroll-behavior:
  // smooth on the document, each of those would start its own browser
  // animation and the result would crawl. 'instant' opts out of that.
  function jumpTo(y) {
    try {
      window.scrollTo({ top: y, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, y);
    }
  }

  function smoothScrollTo(targetY) {
    var startY = window.pageYOffset;
    var distance = targetY - startY;
    if (Math.abs(distance) < 2) return;

    if (REDUCED_MOTION.matches) {
      jumpTo(targetY);
      return;
    }

    var startTime = null;
    var cancelled = false;

    // Hand control straight back if the visitor takes over mid-flight —
    // fighting someone's own wheel or touch input feels broken.
    function cancel() {
      cancelled = true;
    }
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchstart', cancel, { passive: true, once: true });
    window.addEventListener('keydown', cancel, { once: true });

    function step(timestamp) {
      if (cancelled) return;
      if (startTime === null) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / SCROLL_DURATION, 1);
      jumpTo(startY + distance * easeInOutCubic(progress));
      if (progress < 1) window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  function initHero(hero) {
    if (hero.__heroVideoInit) return;
    hero.__heroVideoInit = true;

    var section = hero.closest('.shopify-section') || hero.parentElement;
    var video = hero.querySelector('.hero-video__video');
    var cue = hero.querySelector('[data-hero-scroll]');

    // --- Scroll cue ---------------------------------------------------
    if (cue) {
      cue.addEventListener('click', function () {
        var next = section && section.nextElementSibling;
        var targetY;

        if (next) {
          targetY = window.pageYOffset + next.getBoundingClientRect().top - stickyHeaderOffset();
        } else {
          // Nothing after the hero — fall back to one screen down.
          targetY = window.pageYOffset + window.innerHeight;
        }

        smoothScrollTo(Math.max(0, Math.round(targetY)));
      });
    }

    // --- Scroll-linked exit -------------------------------------------
    // The overlaid controls leave several times faster than the page
    // scrolls, so they have cleared the frame by the time the video has
    // moved appreciably: starting a scroll reads as the content lifting
    // away first, and the video following it up afterwards.
    var inView = true;

    function updateExit() {
      // Distance over which the controls complete their exit. A fraction of
      // the hero rather than a fixed number, so the effect keeps the same
      // proportions on a laptop and on a phone.
      var zone = Math.max(160, Math.min(hero.offsetHeight * 0.4, 420));
      var progress = Math.min(Math.max(window.pageYOffset / zone, 0), 1);

      // Out fast, settling as it goes — the controls should feel pulled
      // away rather than dragged.
      var eased = 1 - Math.pow(1 - progress, 3);

      hero.style.setProperty('--hero-exit-shift', (-160 * eased).toFixed(2) + 'px');
      // Fades ahead of the travel so they are gone before they would reach
      // the header, rather than sliding underneath it.
      hero.style.setProperty('--hero-exit-opacity', Math.max(0, 1 - eased * 1.45).toFixed(3));
      hero.dataset.heroExitComplete = progress > 0.85 ? 'true' : 'false';

      // The load-in animations set opacity and transform themselves, and a
      // running animation outranks these variables. Scrolling during the
      // first couple of seconds would therefore do nothing visible, then
      // snap. Retire them the moment the visitor scrolls at all.
      if (progress > 0) hero.dataset.heroScrolled = 'true';
    }

    if (!REDUCED_MOTION.matches) {
      var ticking = false;

      function onScroll() {
        // One update per frame at most: scroll events fire far more often
        // than the screen refreshes, and each update costs a style recalc.
        if (ticking || !inView) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          updateExit();
          ticking = false;
        });
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      // Browsers restore the previous scroll position on reload, so the
      // hero has to start in the right state rather than at rest.
      updateExit();
    }

    // --- Pause the video off screen ------------------------------------
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
            // Scrolling back up re-enters before the next scroll event
            // lands; refresh now so the controls are not briefly stale.
            if (inView && !REDUCED_MOTION.matches) updateExit();

            if (video) {
              if (entry.isIntersecting) {
                // play() rejects when the browser blocks autoplay; there is
                // nothing to recover, the poster simply stays put.
                var played = video.play();
                if (played && typeof played.catch === 'function') played.catch(function () {});
              } else {
                video.pause();
              }
            }
          });
        },
        { threshold: 0 }
      );

      observer.observe(hero);
    }
  }

  function init() {
    document.querySelectorAll('.hero-video').forEach(initHero);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var hero = event.target.querySelector('.hero-video');
    if (hero) initHero(hero);
  });
})();
